import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateUserRequest, UpdateUserRequest, UserDetailResponse } from '../../api/generated';
import { Table } from '../UI';
import { Button } from '../UI/base/Button';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { useUsers } from '../../hooks/useUsers';
import { useLocations } from '../../hooks/useLocations';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { buildUserErrorMessage } from '../../services/userErrorService';
import { toRoleAssignmentRequest } from '../../services/locationUserService';
import { getUserColumns } from './userColumns';
import { UserFormModal } from './UserFormModal';
import type { UserFormValues } from './UserFormModal';
import { DeleteUserModal } from './DeleteUserModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import styles from './UsersSettings.module.css';

// Locations the user holds no role at are left out entirely rather than sent as
// an empty role set: holding no role is what "not assigned here" means.
const toRoleAssignments = (values: UserFormValues): CreateUserRequest['roleAssignments'] =>
  values.locationRoles
    .filter((entry) => entry.roles.length > 0)
    .map((entry) => toRoleAssignmentRequest(entry.locationId, entry.roles));

export const UsersSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { users, isLoading, loadError, createUser, updateUser, deleteUser, resetPassword } =
    useUsers();
  const { locations } = useLocations();
  const { id: currentUserId } = useCurrentUser();
  const keycloak = useAppSelector((state) => state.authenticationSlice.kcObject);

  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDetailResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserDetailResponse | null>(null);
  const [resetTarget, setResetTarget] = useState<UserDetailResponse | null>(null);

  const columns = useMemo(
    () =>
      getUserColumns(t, {
        currentUserId,
        onEdit: (user) => {
          setEditingUser(user);
          setFormOpen(true);
        },
        onDelete: (user) => setDeleteTarget(user)
      }),
    [t, currentUserId]
  );

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingUser(null);
  };

  const handleSave = async (values: UserFormValues) => {
    const target = editingUser;
    const payload: CreateUserRequest | UpdateUserRequest = {
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      accountAdmin: values.isAccountAdmin,
      roleAssignments: toRoleAssignments(values)
    };
    try {
      if (target) {
        await updateUser(target.userId, payload);
      } else {
        await createUser(payload);
      }
      handleCloseForm();
    } catch (e) {
      console.error('Failed to save user:', e);
      const fallback = target ? t('config-user-update-error') : t('config-user-create-error');
      dispatch(showError({ message: buildUserErrorMessage(e, t, fallback) }));
    }
  };

  // Only one modal may be visible at a time, so the form steps aside for the
  // confirmation and comes back if the admin backs out.
  const handleOpenReset = () => {
    if (!editingUser) return;
    setResetTarget(editingUser);
    setFormOpen(false);
  };

  const handleCancelReset = () => {
    setResetTarget(null);
    setFormOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!resetTarget) return;
    const isSelf = resetTarget.userId === currentUserId;
    try {
      await resetPassword(resetTarget.userId);
      setResetTarget(null);
      setEditingUser(null);
      // The reset invalidated the password this session authenticated with, and the
      // account now owes Keycloak a password change, so the token cannot be renewed.
      if (isSelf) keycloak?.logout({ redirectUri: globalThis.location.origin });
    } catch (e) {
      console.error('Failed to reset password:', e);
      dispatch(showError({ message: buildUserErrorMessage(e, t, t('config-user-reset-error')) }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.userId);
      setDeleteTarget(null);
    } catch (e) {
      console.error('Failed to delete user:', e);
      dispatch(
        showError({ message: buildUserErrorMessage(e, t, t('config-user-delete-error')) })
      );
    }
  };

  const showEmptyState = !isLoading && !loadError && users.length === 0;

  return (
    <section>
      <SectionHeader title={t('config-tab-users')}>
        <Button type="primary" onClick={handleOpenCreate}>
          {t('config-user-add')}
        </Button>
      </SectionHeader>

      {isLoading && users.length === 0 && (
        <p className={styles.emptyState}>{t('config-user-loading')}</p>
      )}

      {loadError && <p className={styles.emptyState}>{t('config-user-load-error')}</p>}

      {showEmptyState && <p className={styles.emptyState}>{t('config-user-empty')}</p>}

      {users.length > 0 && (
        <Table columns={columns} data={users} getRowKey={(u) => u.userId} minWidth={620} />
      )}

      {formOpen && (
        <UserFormModal
          user={editingUser}
          locations={locations}
          onSave={handleSave}
          onResetPassword={handleOpenReset}
          onClose={handleCloseForm}
        />
      )}

      {resetTarget && (
        <ResetPasswordModal
          userName={`${resetTarget.firstName} ${resetTarget.lastName}`.trim()}
          isSelf={resetTarget.userId === currentUserId}
          onConfirm={handleConfirmReset}
          onClose={handleCancelReset}
        />
      )}

      {deleteTarget && (
        <DeleteUserModal
          userName={`${deleteTarget.firstName} ${deleteTarget.lastName}`.trim()}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </section>
  );
};
